'use client';

import { useEffect } from 'react';
import { useRouter } from 'next/navigation';
import { useAuth } from '@/contexts/AuthContext';
import { canView, ModuleKey } from '@/lib/rbac';

interface RoleGuardProps {
  module: ModuleKey;
  children: React.ReactNode;
}

export default function RoleGuard({ module, children }: RoleGuardProps) {
  const { user, loading, role, roleLoading } = useAuth();
  const router = useRouter();

  const isLoading = loading || roleLoading;
  const isAuthenticated = !!user;
  const hasAccess = canView(role, module);

  useEffect(() => {
    if (isLoading) return;
    if (!isAuthenticated) {
      router.replace('/login');
      return;
    }
    if (!hasAccess) {
      router.replace('/access-denied');
    }
  }, [isLoading, isAuthenticated, hasAccess, router]);

  if (isLoading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="flex flex-col items-center gap-3">
          <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
          <p className="text-sm text-muted-foreground">Verifying access…</p>
        </div>
      </div>
    );
  }

  if (!isAuthenticated || !hasAccess) {
    return null;
  }

  return <>{children}</>;
}
